import { Notification } from "@/types";
import { router } from "@inertiajs/react";
import dayjs from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/es";
import relativeTime from "dayjs/plugin/relativeTime";
import { Check, Eye, Trash, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from "../ui/card";
import { urlFor } from "~/client";

// Initialize dayjs with relativeTime plugin
dayjs.extend(relativeTime);

type NotificationWithMessage = Notification & {
  message?: string;
  url?: string | null;
  invitationToken?: string | null;
};

interface NotificationCardProps {
  notification: NotificationWithMessage;
}
function NotificationCard({ notification }: NotificationCardProps) {
  const { id, createdAt, readAt, message, url, invitationToken } = notification;
  const { t, i18n } = useTranslation("notifications");
  const [respondingToInvitation, setRespondingToInvitation] = useState(false);

  // Set dayjs locale based on i18n language
  const currentLocale = i18n.language;
  dayjs.locale(currentLocale || "en");

  // Use dayjs for human readable time
  const humanReadableTime = dayjs(createdAt).fromNow();
  const formattedDate = dayjs(createdAt).format("MMM D, YYYY h:mm A");

  // Ensure notification has an id before creating routes
  if (!id) {
    console.error("Notification is missing id:", notification);
    return null;
  }

  const deleteRoute = urlFor("notifications.delete", { id });

  const handleDelete = () => {
    router.delete(deleteRoute, {
      onSuccess: () => {
        // The notifications prop is lazy on Inertia requests, so explicitly
        // refresh it; redirect().back() alone leaves the bell/list stale.
        router.reload({ only: ["notifications"] });
        toast.success(t("delete.success"));
      },
      onError: () => {
        toast.error(t("delete.error"));
      },
    });
  };

  const handleInvitationResponse = (action: "accept" | "reject") => {
    if (!invitationToken) {
      return;
    }
    router.post(
      urlFor(`invitation-response.${action}`, { token: invitationToken }),
      {},
      {
        onStart: () => setRespondingToInvitation(true),
        onFinish: () => setRespondingToInvitation(false),
        onSuccess: () => {
          router.reload({ only: ["notifications"] });
        },
        onError: () => {
          toast.error(t("invitation.error"));
        },
      },
    );
  };

  const markAsReadRoute = urlFor("notifications.markAsRead", { id });

  const handleMarkAsRead = () => {
    router.visit(markAsReadRoute, {
      onSuccess: () => {
        // The notifications prop is lazy on Inertia requests, so explicitly
        // refresh it; redirect().back() alone leaves the bell/list stale.
        router.reload({ only: ["notifications"] });
        toast.success(t("markAsRead.success"));
      },
      onError: () => {
        toast.error(t("markAsRead.error"));
      },
    });
  };

  // Opening a notification should clear its "new" state — otherwise an unread
  // notification you've already looked at stays flagged as new forever. Mark it
  // read first (best-effort), then navigate to the linked content.
  const handleView = async (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!url || readAt) {
      return;
    }
    event.preventDefault();
    try {
      await fetch(markAsReadRoute, {
        headers: { "X-Requested-With": "XMLHttpRequest" },
        credentials: "same-origin",
      });
    } catch {
      // Navigation still proceeds even if marking it read fails.
    }
    if (url.startsWith("/")) {
      router.visit(url);
    } else {
      window.location.href = url;
    }
  };

  return (
    <Card className="hover:bg-accent hover:text-accent-foreground border-none py-2 shadow-none">
      <CardHeader className="px-2 pb-0">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="line-clamp-2 text-sm leading-snug font-medium">{message}</CardTitle>
          {!readAt && (
            <Badge variant="outline" className="shrink-0">
              {t("badge.new")}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardFooter className="flex items-center justify-between px-2">
        <CardDescription className="text-xs">
          <span title={formattedDate}>{humanReadableTime}</span>
        </CardDescription>
        <div className="flex gap-2">
          {invitationToken ? (
            <>
              <Button
                size="sm"
                className="h-8 cursor-pointer"
                disabled={respondingToInvitation}
                onClick={() => handleInvitationResponse("accept")}
              >
                <Check className="mr-1 h-4 w-4" />
                {t("invitation.accept")}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-8 cursor-pointer"
                disabled={respondingToInvitation}
                onClick={() => handleInvitationResponse("reject")}
              >
                <X className="mr-1 h-4 w-4" />
                {t("invitation.reject")}
              </Button>
            </>
          ) : (
            <>
              {url && (
                <a href={url} onClick={handleView}>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 cursor-pointer"
                    title={t("buttons.view")}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </a>
              )}
              {!readAt && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleMarkAsRead}
                  className="h-8 w-8 cursor-pointer"
                  title={t("buttons.markAsRead")}
                >
                  <Check className="h-4 w-4" />
                </Button>
              )}
            </>
          )}
          <Button
            variant="destructive"
            size="icon"
            onClick={handleDelete}
            className="h-8 w-8 cursor-pointer"
            title={t("buttons.delete")}
          >
            <Trash className="h-4 w-4" />
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}

export default NotificationCard;
